import React, { useCallback, useMemo } from 'react';
import dayjs from 'src/utils/dayjs';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Autocomplete, Box, Button, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import generateNumbersList from 'src/utils/generateNumbersList';
import { getGracePeriodFromMonthDiff } from 'src/utils/contract';
import getMonthDiff from 'src/utils/date';
import { useSelector } from 'react-redux';
import { selectDistributorDetail } from 'src/store/selectors/distributorSelectors';
import { GracePeriodValues } from 'src/utils/constants/distributors';
import {
  calculateEndDateFromGracePeriod,
  calculateFirstRentDateFromGracePeriod,
  calculateSecondRentDateFromGracePeriod,
  setGracePeriodForFirstUpfrontRentModalityMonthsDiff
} from 'src/utils/contracts';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const getMaxDateForSecondRentDate = (maxGracePeriod, startDate) => {
  if (maxGracePeriod <= GracePeriodValues.THIRTY) {
    return momentLocalTimezone(startDate).add(2, 'months');
  }
  const monthsToAdd = (Number(maxGracePeriod) + 30) / 30;
  return momentLocalTimezone(startDate).add(
    monthsToAdd < 4 ? monthsToAdd : 4,
    'months'
  );
};

const calculateRentDatesAndGracePeriod = values => {
  let { firstRentDate } = values;
  const rawMonthDiff = getMonthDiff(values.startDate, values.firstRentDate);
  const firstUpfrontRentModalityMonthsDiff = getMonthDiff(
    values.startDate,
    values.secondRentDate
  );
  let gracePeriod = 0;

  if (values.isFirstUpfrontRentModality) {
    gracePeriod = setGracePeriodForFirstUpfrontRentModalityMonthsDiff(
      firstUpfrontRentModalityMonthsDiff
    );
    const { secondRentDate } = values;
    firstRentDate = dayjs(secondRentDate).subtract(1, 'months');
  } else {
    gracePeriod = getGracePeriodFromMonthDiff(rawMonthDiff);
  }

  return { gracePeriod, firstRentDate };
};

const StepFour = ({ data, handleNext, handleBack, resetRef }) => {
  const { classes } = useStyles();
  const parameter = useSelector(selectDistributorDetail);
  const initialState = useMemo(
    () =>
      makeInitialState(
        {
          ...data
        },
        parameter
      ),
    [data]
  );
  const { t } = useTranslation();

  const onNext = useCallback(
    values => {
      const { gracePeriod, firstRentDate } = calculateRentDatesAndGracePeriod(
        values
      );
      handleNext({ ...values, gracePeriod, firstRentDate });
    },
    [handleNext]
  );

  const handleStartDateChange = (newValue, values, setFieldValue) => {
    setFieldValue('startDate', momentLocalTimezone(newValue));
    setFieldValue('payday', Math.min(momentLocalTimezone(newValue).date(), 25));
    setFieldValue(
      'endDate',
      calculateEndDateFromGracePeriod(
        momentLocalTimezone(newValue),
        values.term,
        values.gracePeriod,
        data.isFirstUpfrontRentModality
      )
    );

    if (values.isFirstUpfrontRentModality) {
      setFieldValue(
        'secondRentDate',
        calculateSecondRentDateFromGracePeriod(
          momentLocalTimezone(newValue),
          values.gracePeriod
        )
      );
      return;
    }

    setFieldValue(
      'firstRentDate',
      calculateFirstRentDateFromGracePeriod(
        momentLocalTimezone(newValue),
        values.gracePeriod,
        data.isFirstUpfrontRentModality,
        Number(values.initialPayment)
      )
    );
  };

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        errors,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        resetForm
      }) => {
        resetRef.current = () => resetForm();
        return (
          <form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <DatePicker
                label={t('CONTRACT_VIEW.SIGNATURE_DATE')}
                format="DD/MM/YYYY"
                value={values.startDate}
                onChange={newValue => {
                  handleStartDateChange(newValue, values, setFieldValue);
                }}
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.startDate && errors.startDate),
                    helperText: touched.startDate && t(errors.startDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />

              {!data.isFirstUpfrontRentModality && (
                <DatePicker
                  label={t('CONTRACT_VIEW.FIRST_RENT_DATE')}
                  value={values.firstRentDate}
                  format="DD/MM/YYYY"
                  maxDate={dayjs.tz().add(4, 'months')}
                  minDate={values.startDate}
                  onChange={newValue => {
                    setFieldValue(
                      'firstRentDate',
                      momentLocalTimezone(newValue)
                    );
                    setFieldValue(
                      'endDate',
                      momentLocalTimezone(newValue).add(data.term - 1, 'months')
                    );
                  }}
                  disabled={
                    data.enableMsi ||
                    Number(data.initialPayment) === 0 ||
                    data.isFirstUpfrontRentModality
                  }
                  slotProps={{
                    textField: {
                      sx: { width: '100%', margin: '16px 0' },
                      error: Boolean(touched.firstRentDate && errors.firstRentDate),
                      helperText: touched.firstRentDate && t(errors.firstRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />
              )}

              {data.isFirstUpfrontRentModality && (
                <DatePicker
                  label={t('CONTRACT_VIEW.SECOND_RENT_DATE')}
                  value={values.secondRentDate}
                  format="DD/MM/YYYY"
                  maxDate={getMaxDateForSecondRentDate(
                    parameter.maxGracePeriod,
                    values.startDate
                  )}
                  minDate={momentLocalTimezone(values.startDate).add(
                    2,
                    'months'
                  )}
                  onChange={newValue => {
                    setFieldValue(
                      'secondRentDate',
                      momentLocalTimezone(newValue)
                    );
                    setFieldValue(
                      'endDate',
                      momentLocalTimezone(newValue).add(data.term - 2, 'months')
                    );
                  }}
                  disabled={
                    Number(parameter.maxGracePeriod) <= GracePeriodValues.THIRTY
                  }
                  slotProps={{
                    textField: {
                      sx: { width: '100%', margin: '16px 0' },
                      error: Boolean(touched.secondRentDate && errors.secondRentDate),
                      helperText: touched.secondRentDate && t(errors.secondRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />
              )}
              <Autocomplete
                options={generateNumbersList(1, 25)}
                id="payday"
                label={t('NAVIGATION.PAY_DAY')}
                value={values.payday}
                fullWidth
                getOptionLabel={option => String(option)}
                onChange={(_, newValue) => {
                  setFieldValue('payday', newValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('NAVIGATION.PAY_DAY')}
                    margin="normal"
                    fullWidth
                    error={Boolean(touched.payday && errors.payday)}
                    helperText={touched.payday && t(errors.payday)}
                  />
                )}
              />
              <DatePicker
                label={t('CONTRACT_VIEW.LAST_RENT_DATE')}
                value={values.endDate}
                disabled
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.endDate && errors.endDate),
                    helperText: touched.endDate && t(errors.endDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />
            </Box>

            <Box
              className={classes.box}
              display="flex"
              justifyContent="space-between"
            >
              <Button
                className={classes.buttons}
                onClick={() => handleBack()}
                color="primary"
                variant="outlined"
              >
                {t('COMMON.BACK')}
              </Button>
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};
StepFour.propTypes = {
  data: PropTypes.object,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  resetRef: PropTypes.object
};

export default StepFour;
